/**
 * Printable chargeback statement for one department.
 *
 * Opens as an overlay with a Print button that hands off to the browser's own
 * print dialog, from which the user can Save as PDF. No PDF library: the
 * browser's engine already lays out and paginates HTML correctly, and a
 * generated PDF would mean owning page breaks, fonts and margins for a document
 * whose only job is to be readable and signed off.
 *
 * Stops at account -> service. A statement enumerating every resource would run
 * to hundreds of pages for a real department, and a document nobody reads to the
 * end cannot be approved. Resource-level detail lives in the drill-down.
 */
import { useEffect, useState } from 'react';
import { X, Printer, Loader2 } from 'lucide-react';

interface ServiceRow {
  key: string;
  label: string;
  cost: number;
  percentage: number;
}

interface AccountRow extends ServiceRow {
  provider?: string;
  services: ServiceRow[];
}

interface Props {
  apiBase: string;
  authHeaders: () => Record<string, string>;
  department: string;
  month: string;
  provider: string;
  onClose: () => void;
}

const money = (v: number) => `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function DepartmentStatement({ apiBase, authHeaders, department, month, provider, onClose }: Props) {
  const [data, setData] = useState<{ total: number; accounts: AccountRow[]; period?: any } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const qs = new URLSearchParams({ month });
        if (provider && provider !== 'all') qs.set('provider', provider);
        const res = await fetch(
          `${apiBase}/tagging/chargeback/statement/${encodeURIComponent(department)}?${qs}`,
          { headers: authHeaders() }
        );
        const body = await res.json();
        if (!res.ok) throw new Error(body.errors?.[0]?.message || `HTTP ${res.status}`);
        setData(body.data);
      } catch (e: any) {
        setErr(e.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [department, month, provider]);

  return (
    <>
      {/* Only the statement itself survives printing - the overlay chrome, the
          app shell and the buttons are all suppressed by the rules below. */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #dept-statement, #dept-statement * { visibility: visible !important; }
          #dept-statement {
            position: absolute !important;
            left: 0; top: 0; width: 100%;
            max-height: none !important;
            overflow: visible !important;
            box-shadow: none !important;
            border: 0 !important;
          }
          .no-print { display: none !important; }
          thead { display: table-header-group; }
          tr { break-inside: avoid; }
        }
      `}</style>

      <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 no-print-bg">
        <div
          id="dept-statement"
          className="my-8 w-full max-w-3xl rounded-lg bg-white p-8 text-black shadow-xl"
        >
          <div className="mb-6 flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold">Chargeback Statement</h1>
              <p className="mt-1 text-sm text-gray-600">
                {department} · {month}
              </p>
              {provider && provider !== 'all' && (
                <p className="text-sm text-gray-600">Provider: {provider.toUpperCase()}</p>
              )}
            </div>
            <div className="no-print flex gap-2">
              <button
                onClick={() => window.print()}
                disabled={!data}
                className="inline-flex items-center gap-1 rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
              >
                <Printer className="h-4 w-4" /> Print
              </button>
              <button onClick={onClose} className="rounded border px-2 py-1.5 text-sm" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {err && <p className="text-sm text-rose-600">{err}</p>}

          {!data && !err && (
            <p className="inline-flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin" /> Preparing statement…
            </p>
          )}

          {data && (
            <>
              <div className="mb-6 rounded border border-gray-300 p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">Total chargeable</p>
                <p className="text-3xl font-bold">{money(data.total)}</p>
              </div>

              {data.accounts.map((a) => (
                <div key={a.key} className="mb-5">
                  <div className="flex items-baseline justify-between border-b border-gray-300 pb-1">
                    <h2 className="font-semibold">
                      {a.provider ? `${a.provider.toUpperCase()} · ` : ''}
                      {a.label}
                    </h2>
                    <span className="tabular-nums font-semibold">{money(a.cost)}</span>
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {a.services.map((s) => (
                        <tr key={s.key}>
                          <td className="py-1 pl-4 text-gray-700">{s.label}</td>
                          <td className="py-1 text-right tabular-nums text-gray-700">{money(s.cost)}</td>
                          <td className="w-16 py-1 text-right tabular-nums text-gray-500">{s.percentage}%</td>
                        </tr>
                      ))}
                      {a.services.length === 0 && (
                        <tr>
                          <td className="py-1 pl-4 text-gray-500" colSpan={3}>
                            No service breakdown available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ))}

              {data.accounts.length === 0 && (
                <p className="text-sm text-gray-600">No costs recorded for this department in this period.</p>
              )}

              <div className="mt-8 border-t border-gray-300 pt-3 text-xs text-gray-500">
                <p>
                  Generated {new Date().toLocaleString()} · Costs are unblended unless otherwise stated.
                </p>
                <p>
                  Spend without a Department tag is not included here and is reported separately as
                  Unallocated.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
